import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getTeacherProfiles,
  createTeacherProfile,
  updateTeacherProfile,
  deleteTeacherProfile,
  getTeacherSubjects,
  assignTeacherSubject,
  unassignTeacherSubject,
} from "../../../services/TeacherProfileService";
import { getUsers } from "../../../services/UserService";
import { getDepartments } from "../../../services/DepartmentService";
import { getSubjects } from "../../../services/SubjectService";
import DataTablePagination from "../common/DataTablePagination";
import { Trans, useTranslation } from "react-i18next";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";
import { getLocale } from "../../../utils/i18nLabels";

const emptyForm = {
  accountMode: "new", // "new" = créer le compte en même temps | "existing" = attacher un compte déjà créé
  user_id: "",
  email: "",
  password: "",
  first_name: "",
  last_name: "",
  phone: "",
  nni: "",
  gender: "",
  date_of_birth: "",
  place_of_birth: "",
  nationality: "",
  speciality: "",
  department_id: "",
  // Champs RH constatés dans le canevas réel des enseignants.
  matricule: "",
  employment_status: "",
  academic_grade: "",
  hire_year: "",
  administrative_responsibility: "",
  position_status: "",
  highest_diploma: "",
  diploma_country: "",
  training_speciality: "",
};

const Teachers = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.TEACHER_PROFILE_CREATE);
  const canUpdate = can(PERMISSIONS.TEACHER_PROFILE_UPDATE);
  const canDelete = can(PERMISSIONS.TEACHER_PROFILE_DELETE);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subjects, setSubjects] = useState([]);

  // Matières assignées ("un enseignant peut avoir uniquement ses propres
  // matières") - modal dédiée, indépendante du formulaire d'édition.
  const [subjectsModalTarget, setSubjectsModalTarget] = useState(null);
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectToAdd, setSubjectToAdd] = useState("");
  const [subjectsActionPending, setSubjectsActionPending] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [viewMode, setViewMode] = useState("grid");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    getUsers({ page: 1, limit: 200, role: "TEACHER" }).then((res) => setUsers(res.data?.data || [])).catch(() => {});
    getDepartments({ page: 1, limit: 200 }).then((res) => setDepartments(res.data?.data || [])).catch(() => {});
    getSubjects({ page: 1, limit: 200 }).then((res) => setSubjects(res.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "created_at", sort_dir: "desc" };
      if (departmentFilter) params.department_id = departmentFilter;

      const res = await getTeacherProfiles(params);
      const body = res.data;
      setProfiles(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.teachers.impossibleChargerListeEnseignants"));
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, departmentFilter, t]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const userLabel = (userId) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return userId;
    const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
    return name ? `${name} (${user.email})` : user.email;
  };

  const departmentLabel = (id) => departments.find((d) => d.id === id)?.name || "-";

  const visibleProfiles = search
    ? profiles.filter((p) => userLabel(p.user_id).toLowerCase().includes(search.toLowerCase()))
    : profiles;

  const usersWithoutProfile = users.filter((u) => !profiles.some((p) => p.user_id === u.id));

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setDepartmentFilter("");
    setPage(1);
  };

  const openAddModal = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = (profile) => {
    setFormMode("edit");
    setFormData({
      user_id: profile.user_id || "",
      speciality: profile.speciality || "",
      department_id: profile.department_id || "",
      matricule: profile.matricule || "",
      employment_status: profile.employment_status || "",
      academic_grade: profile.academic_grade || "",
      hire_year: profile.hire_year ?? "",
      administrative_responsibility: profile.administrative_responsibility || "",
      position_status: profile.position_status || "",
      highest_diploma: profile.highest_diploma || "",
      diploma_country: profile.diploma_country || "",
      training_speciality: profile.training_speciality || "",
    });
    setFormErrors({});
    setSubmitError("");
    setEditingId(profile.id);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (formMode === "add") {
      if (formData.accountMode === "existing") {
        if (!formData.user_id) errors.user_id = t("common.ui.leCompteUtilisateurEstRequis");
      } else {
        if (!formData.first_name.trim()) errors.first_name = t("common.ui.lePrenomEstRequis");
        if (!formData.last_name.trim()) errors.last_name = t("common.ui.leNomEstRequis");
        if (!formData.email.trim()) errors.email = t("common.ui.lEmailEstRequis");
        if (!formData.password || formData.password.length < 6)
          errors.password = t("common.ui.leMotPasseDoitContenirMoins");
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const hrFieldsPayload = () => ({
    matricule: formData.matricule.trim() || null,
    employment_status: formData.employment_status || null,
    academic_grade: formData.academic_grade.trim() || null,
    hire_year: formData.hire_year === "" ? null : Number(formData.hire_year),
    administrative_responsibility: formData.administrative_responsibility.trim() || null,
    position_status: formData.position_status.trim() || null,
    highest_diploma: formData.highest_diploma.trim() || null,
    diploma_country: formData.diploma_country.trim() || null,
    training_speciality: formData.training_speciality.trim() || null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      if (formMode === "add") {
        const payload =
          formData.accountMode === "existing"
            ? { user_id: formData.user_id }
            : {
                email: formData.email.trim(),
                password: formData.password,
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
                phone: formData.phone.trim() || null,
                nni: formData.nni.trim() || null,
                gender: formData.gender || null,
                date_of_birth: formData.date_of_birth || null,
                place_of_birth: formData.place_of_birth.trim() || null,
                nationality: formData.nationality.trim() || null,
              };
        await createTeacherProfile({
          ...payload,
          speciality: formData.speciality.trim() || null,
          department_id: formData.department_id || null,
          ...hrFieldsPayload(),
        });
        setFeedback({
          type: "success",
          message:
            formData.accountMode === "existing"
              ? t("pages.teachers.createProfileSuccess")
              : t("pages.teachers.createAccountAndProfileSuccess"),
        });
      } else {
        await updateTeacherProfile(editingId, {
          speciality: formData.speciality.trim() || null,
          department_id: formData.department_id || null,
          ...hrFieldsPayload(),
        });
        setFeedback({ type: "success", message: t("pages.teachers.updateSuccess") });
      }
      setShowFormModal(false);
      fetchProfiles();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("common.crud.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const openSubjectsModal = async (profile) => {
    setSubjectsModalTarget(profile);
    setSubjectToAdd("");
    setSubjectsLoading(true);
    try {
      const res = await getTeacherSubjects(profile.id);
      setTeacherSubjects(res.data?.data || []);
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.teachers.impossibleChargerMatieresAssignees") });
    } finally {
      setSubjectsLoading(false);
    }
  };

  const closeSubjectsModal = () => {
    if (subjectsActionPending) return;
    setSubjectsModalTarget(null);
    setTeacherSubjects([]);
  };

  const handleAddSubject = async () => {
    if (!subjectToAdd || !subjectsModalTarget) return;
    setSubjectsActionPending(true);
    try {
      const res = await assignTeacherSubject(subjectsModalTarget.id, subjectToAdd);
      setTeacherSubjects(res.data?.data || []);
      setSubjectToAdd("");
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.teachers.impossibleAssignerMatiere") });
    } finally {
      setSubjectsActionPending(false);
    }
  };

  const handleRemoveSubject = async (subjectId) => {
    if (!subjectsModalTarget) return;
    setSubjectsActionPending(true);
    try {
      const res = await unassignTeacherSubject(subjectsModalTarget.id, subjectId);
      setTeacherSubjects(res.data?.data || []);
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.teachers.impossibleRetirerMatiere") });
    } finally {
      setSubjectsActionPending(false);
    }
  };

  const availableSubjectsToAdd = subjects.filter((s) => !teacherSubjects.some((ts) => ts.subject_id === s.id));

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteTeacherProfile(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.teachers.deleteSuccess") });
      setDeleteTarget(null);
      if (profiles.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchProfiles();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("common.ui.impossibleSupprimerProfil") });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("menu.teachers.title")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            <div className="mb-2">
              {canCreate && (
<button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.teachers.addLabel")}
              </button>
)}
            </div>
          </div>
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="alert alert-info">
          <Trans i18nKey="pages.teachers.infoBanner" components={[<strong />, <Link to="/users" />]} />
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("menu.teachers.list")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <span className="icon-addon"></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("common.ui.rechercherNomEmail")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ paddingLeft: "35px", minWidth: "220px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={departmentFilter}
                  onChange={(e) => {
                    setDepartmentFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.fields.allDepartments")}</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex align-items-center bg-white border rounded-2 p-1 mb-3">
                <button
                  type="button"
                  className={`btn btn-icon btn-sm me-1 ${viewMode === "list" ? "bg-light" : ""}`}
                  onClick={() => setViewMode("list")}
                  title={t("common.ui.vueListe")}
                >
                  <i className="ti ti-list-tree"></i>
                </button>
                <button
                  type="button"
                  className={`btn btn-icon btn-sm ${viewMode === "grid" ? "bg-light" : ""}`}
                  onClick={() => setViewMode("grid")}
                  title={t("common.ui.vueGrille")}
                >
                  <i className="ti ti-grid-dots"></i>
                </button>
              </div>
            </div>
          </div>

          <DataTablePagination
            variant="header"
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={(v) => {
              setLimit(v);
              setPage(1);
            }}
          />

          <div className="card-body p-0 py-3">
            {viewMode === "grid" ? (
              <div className="row px-3">
                {loading ? (
                  <div className="col-12 text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">{t("common.crud.loading")}</span>
                    </div>
                  </div>
                ) : loadError ? (
                  <div className="col-12 text-center py-4 text-danger">{loadError}</div>
                ) : visibleProfiles.length > 0 ? (
                  visibleProfiles.map((profile) => {
                    const account = users.find((u) => u.id === profile.user_id);
                    const name =
                      [account?.first_name, account?.last_name].filter(Boolean).join(" ") ||
                      account?.email ||
                      profile.speciality;
                    const inactive = account?.is_active === false;
                    return (
                      <div className="col-xxl-3 col-xl-4 col-md-6 mb-4 d-flex" key={profile.id}>
                        <div className="card flex-fill mb-0 h-auto shadow-sm" style={{ minWidth: 0 }}>
                          <div className="card-header d-flex align-items-center justify-content-between">
                            <span className="fw-medium">{profile.speciality || "-"}</span>
                            <div className="d-flex align-items-center">
                              <span
                                className={`badge badge-soft-${inactive ? "danger" : "success"} d-inline-flex align-items-center me-1`}
                              >
                                <i className="ti ti-circle-filled fs-5 me-1"></i>
                                {inactive ? t("common.crud.statusInactive") : t("common.crud.statusActive")}
                              </span>
                              <Dropdown>
                                <Dropdown.Toggle
                                  as="button"
                                  className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                                >
                                  <i className="ti ti-dots-vertical fs-14"></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end" className="p-3">
                                  <Dropdown.Item as={Link} className="rounded-1" to={`/teacher/${profile.id}`}>
                                    <i className="ti ti-eye me-2"></i>{t("common.crud.viewDetails")}
                                  </Dropdown.Item>
                                  {canUpdate && (
<Dropdown.Item className="rounded-1" onClick={() => openEditModal(profile)}>
                                    <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                                  </Dropdown.Item>
)}
                                  <Dropdown.Item className="rounded-1" onClick={() => openSubjectsModal(profile)}>
                                    <i className="ti ti-book me-2"></i>{t("pages.teachers.matieresAssignees")}
                                  </Dropdown.Item>
                                  {canDelete && (
<Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(profile)}>
                                    <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                                  </Dropdown.Item>
)}
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </div>
                          <div className="card-body">
                            <div className="bg-light rounded-2 p-3 mb-3">
                              <div className="d-flex flex-column  align-items-center">
                                <span className="avatar avatar-lg flex-shrink-0 bg-primary-transparent rounded-circle d-flex align-items-center justify-content-center">
                                  {account?.avatar_url ? (
                                    <img src={account.avatar_url} className="img-fluid rounded-circle" alt={name} />
                                  ) : (
                                    <span className="text-primary fw-semibold">
                                      {name.slice(0, 2).toUpperCase()}
                                    </span>
                                  )}
                                </span>
                                <div className="w-100 mt-2 text-center overflow-hidden">
                                  <h6 className="mb-0 text-truncate" title={name}>{name}</h6>
                                  <p className="mb-0 text-muted text-truncate">
                                    {profile.department_id ? departmentLabel(profile.department_id) : "-"}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <div>
                                <p className="mb-0 text-muted">{t("common.crud.enrolledOn")}</p>
                                <p className="text-dark mb-0">
                                  {profile.created_at
                                    ? new Date(profile.created_at).toLocaleDateString(getLocale())
                                    : "-"}
                                </p>
                              </div>
                            </div>
                            <div className="mt-3">
                              <p className="mb-1 text-muted text-truncate">
                                <i className="ti ti-phone me-1"></i>
                                {account?.phone || "-"}
                              </p>
                              <p className="mb-0 text-muted text-truncate" title={account?.email || ""}>
                                <i className="ti ti-mail me-1"></i>
                                {account?.email || "-"}
                              </p>
                            </div>
                            <Link
                              to={`/teacher/${profile.id}`}
                              className="btn btn-outline-primary btn-sm w-100 mt-3"
                            >
                              <i className="ti ti-eye me-1"></i>{t("common.crud.viewDetail")}
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-12 text-center py-4">
                    <i className="ti ti-search-off fs-1 text-muted"></i>
                    <h5 className="mt-2">{t("pages.teachers.noTeachersFound")}</h5>
                  </div>
                )}
              </div>
            ) : (
            <div className="custom-datatable-filter table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("roles.TEACHER")}</th>
                    <th>{t("common.fields.speciality")}</th>
                    <th>{t("common.fields.department")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="4" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : visibleProfiles.length > 0 ? (
                    visibleProfiles.map((profile) => {
                      const account = users.find((u) => u.id === profile.user_id);
                      const name =
                        [account?.first_name, account?.last_name].filter(Boolean).join(" ") ||
                        account?.email ||
                        "-";
                      return (
                      <tr key={profile.id}>
                        <td className="fw-medium">
                          <div className="d-flex align-items-center">
                            <span className="avatar avatar-sm flex-shrink-0 bg-primary-transparent rounded-circle d-flex align-items-center justify-content-center me-2">
                              {account?.avatar_url ? (
                                <img src={account.avatar_url} className="img-fluid rounded-circle" alt={name} />
                              ) : (
                                <span className="text-primary fw-semibold fs-12">
                                  {name.slice(0, 2).toUpperCase()}
                                </span>
                              )}
                            </span>
                            <span>{userLabel(profile.user_id)}</span>
                          </div>
                        </td>
                        <td>{profile.speciality || "-"}</td>
                        <td>{profile.department_id ? departmentLabel(profile.department_id) : "-"}</td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                            >
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              <Dropdown.Item as={Link} className="rounded-1" to={`/teacher/${profile.id}`}>
                                <i className="ti ti-eye me-2"></i>{t("common.crud.viewDetails")}
                              </Dropdown.Item>
                              {canUpdate && (
<Dropdown.Item className="rounded-1" onClick={() => openEditModal(profile)}>
                                <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                              </Dropdown.Item>
)}
                              <Dropdown.Item className="rounded-1" onClick={() => openSubjectsModal(profile)}>
                                <i className="ti ti-book me-2"></i>{t("pages.teachers.matieresAssignees")}
                              </Dropdown.Item>
                              {canDelete && (
<Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(profile)}>
                                <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                              </Dropdown.Item>
)}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.teachers.noTeachersFound")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            )}

            <DataTablePagination
              variant="footer"
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onLimitChange={(v) => {
                setLimit(v);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{formMode === "add" ? t("pages.teachers.addLabel") : t("pages.teachers.editTitle")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            {formMode === "add" && (
              <>
                <div className="mb-3">
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${formData.accountMode === "new" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => handleFormChange("accountMode", "new")}
                    >
                      {t("common.crud.newAccount")}
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${formData.accountMode === "existing" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => handleFormChange("accountMode", "existing")}
                    >
                      {t("common.crud.existingAccount")}
                    </button>
                  </div>
                </div>

                {formData.accountMode === "new" ? (
                  <>
                    <div className="row">
                      <div className="col-sm-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {t("common.fields.firstName")} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.first_name ? "is-invalid" : ""}`}
                            value={formData.first_name}
                            onChange={(e) => handleFormChange("first_name", e.target.value)}
                          />
                          {formErrors.first_name && <div className="invalid-feedback">{formErrors.first_name}</div>}
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {t("common.fields.name")} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.last_name ? "is-invalid" : ""}`}
                            value={formData.last_name}
                            onChange={(e) => handleFormChange("last_name", e.target.value)}
                          />
                          {formErrors.last_name && <div className="invalid-feedback">{formErrors.last_name}</div>}
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("common.fields.email")} <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className={`form-control ${formErrors.email ? "is-invalid" : ""}`}
                        value={formData.email}
                        onChange={(e) => handleFormChange("email", e.target.value)}
                      />
                      {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                    </div>
                    <div className="row">
                      <div className="col-sm-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {t("common.fields.password")} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="password"
                            className={`form-control ${formErrors.password ? "is-invalid" : ""}`}
                            value={formData.password}
                            onChange={(e) => handleFormChange("password", e.target.value)}
                          />
                          {formErrors.password && <div className="invalid-feedback">{formErrors.password}</div>}
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="mb-3">
                          <label className="form-label">{t("common.fields.phone")}</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.phone}
                            onChange={(e) => handleFormChange("phone", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label className="form-label">{t("common.ui.sexe")}</label>
                        <select
                          className="form-select"
                          value={formData.gender}
                          onChange={(e) => handleFormChange("gender", e.target.value)}
                        >
                          <option value="">-</option>
                          <option value="M">{t("common.ui.masculin")}</option>
                          <option value="F">{t("common.ui.feminin")}</option>
                        </select>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">{t("common.ui.dateNaissance")}</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.date_of_birth}
                          onChange={(e) => handleFormChange("date_of_birth", e.target.value)}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">{t("common.ui.lieuNaissance")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.place_of_birth}
                          onChange={(e) => handleFormChange("place_of_birth", e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t("common.ui.nniNumeroNationalIdentite")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.nni}
                          onChange={(e) => handleFormChange("nni", e.target.value)}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t("common.ui.nationalite")}</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={t("common.ui.exMauritanienne")}
                          value={formData.nationality}
                          onChange={(e) => handleFormChange("nationality", e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="mb-3">
                    <label className="form-label">
                      {t("pages.teachers.existingAccountLabel")} <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.user_id ? "is-invalid" : ""}`}
                      value={formData.user_id}
                      onChange={(e) => handleFormChange("user_id", e.target.value)}
                    >
                      <option value="">{t("common.crud.selectPlaceholder")}</option>
                      {usersWithoutProfile.map((u) => (
                        <option key={u.id} value={u.id}>
                          {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email} ({u.email})
                        </option>
                      ))}
                    </select>
                    {formErrors.user_id && <div className="invalid-feedback d-block">{formErrors.user_id}</div>}
                    {usersWithoutProfile.length === 0 && (
                      <div className="form-text">
                        {t("pages.teachers.noAvailableAccounts")}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            <div className="mb-3">
              <label className="form-label">{t("common.fields.speciality")}</label>
              <input
                type="text"
                className="form-control"
                placeholder={t("pages.teachers.mathematiques")}
                value={formData.speciality}
                onChange={(e) => handleFormChange("speciality", e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">{t("common.fields.department")}</label>
              <select
                className="form-select"
                value={formData.department_id}
                onChange={(e) => handleFormChange("department_id", e.target.value)}
              >
                <option value="">{t("common.fields.noneMasc")}</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <hr />
            <h6 className="fw-bold mb-2">{t("common.ui.informationsRh")}</h6>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.fields.matricule")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.matricule}
                  onChange={(e) => handleFormChange("matricule", e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.crud.statusColumn")}</label>
                <select
                  className="form-select"
                  value={formData.employment_status}
                  onChange={(e) => handleFormChange("employment_status", e.target.value)}
                >
                  <option value="">-</option>
                  <option value="permanent">{t("pages.teachers.permanent")}</option>
                  <option value="contractual">{t("pages.teachers.contractuel")}</option>
                  <option value="vacataire">{t("pages.teachers.vacataire")}</option>
                </select>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.teachers.grade")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.academic_grade}
                  onChange={(e) => handleFormChange("academic_grade", e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.teachers.anneeEngagement")}</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.hire_year}
                  onChange={(e) => handleFormChange("hire_year", e.target.value)}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.teachers.responsabiliteAdminPedagogique")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.teachers.exCoordinateurChefDepartement")}
                  value={formData.administrative_responsibility}
                  onChange={(e) => handleFormChange("administrative_responsibility", e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.teachers.position")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.teachers.exDetachementDisponibilite")}
                  value={formData.position_status}
                  onChange={(e) => handleFormChange("position_status", e.target.value)}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">{t("pages.teachers.diplomePlusEleve")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.highest_diploma}
                  onChange={(e) => handleFormChange("highest_diploma", e.target.value)}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t("pages.teachers.paysObtention")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.diploma_country}
                  onChange={(e) => handleFormChange("diploma_country", e.target.value)}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t("pages.teachers.specialiteFormation")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.training_speciality}
                  onChange={(e) => handleFormChange("training_speciality", e.target.value)}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : formMode === "add" ? t("common.crud.add") : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={subjectsModalTarget !== null} onHide={closeSubjectsModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("pages.teachers.matieresAssignees")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            {t("pages.teachers.unEnseignantNePeutCreerModifier")}
          </p>
          {subjectsLoading ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("common.crud.loading")}</span>
              </div>
            </div>
          ) : (
            <>
              {teacherSubjects.length === 0 ? (
                <p className="text-muted mb-3">{t("pages.teachers.aucuneMatiereAssigneePourMoment")}</p>
              ) : (
                <ul className="list-group mb-3">
                  {teacherSubjects.map((ts) => (
                    <li key={ts.id} className="list-group-item d-flex justify-content-between align-items-center">
                      {ts.name}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={subjectsActionPending}
                        onClick={() => handleRemoveSubject(ts.subject_id)}
                      >
                        <i className="ti ti-x"></i>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="d-flex gap-2">
                <select
                  className="form-select"
                  value={subjectToAdd}
                  onChange={(e) => setSubjectToAdd(e.target.value)}
                >
                  <option value="">{t("pages.teachers.selectionnerMatiere")}</option>
                  {availableSubjectsToAdd.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-primary flex-shrink-0"
                  disabled={!subjectToAdd || subjectsActionPending}
                  onClick={handleAddSubject}
                >
                  {t("common.crud.add")}
                </button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={closeSubjectsModal}>
            {t("common.crud.close")}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p>{t("pages.teachers.confirmDeleteBody")}</p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.crud.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Teachers;
